/**
 * Driver Themes ticket — Neon fragment shader.
 *
 * Standalone program for the Neon family. Renders a synthwave
 * landscape: three-stop sky gradient (zenith / mid / horizon),
 * twinkling stars in the upper portion, a rising sun with
 * horizontal stripe carve-outs in its lower half, two procedural
 * mountain silhouette layers, and a foreground wireframe grid
 * rendered via inverse perspective projection.
 *
 * Driven by three uniforms: uIntensity (mapped from session power
 * kW) controls overall motion speed + final brightness; uKwhScale
 * (mapped from kWh delivered) drives the sun's vertical position
 * + radius, so the sun visibly rises and grows as the session
 * accumulates energy; uTapRise (0→1, the host's smoothed tap
 * impulse) lifts the sun toward its full-charge position on tap
 * and lets it ease back to the charge-determined height — the
 * Neon tap interaction. 0 when untouched, so the baseline
 * composition is unchanged.
 *
 * Body is verbatim from the ticket spec except the tap-rise term;
 * do not refactor beyond that.
 */
export const NEON_SHADER_SOURCE = `#version 300 es
precision highp float;
uniform float iTime;
uniform vec2 iResolution;
uniform float uIntensity;
uniform float uKwhScale;
uniform float uTapRise;
out vec4 fragColor;
float hash21(vec2 p) {
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}
float vnoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1, 0));
  float c = hash21(i + vec2(0, 1));
  float d = hash21(i + vec2(1, 1));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float r = 0.0; float a = 0.5;
  for (int i = 0; i < 3; i++) { r += vnoise(p) * a; p *= 2.0; a *= 0.5; }
  return r;
}
float mountainSilhouette(float x, float layerSeed, float scale, float baseY) {
  float h = fbm(vec2(x * 1.2 + layerSeed, layerSeed * 0.3)) * scale;
  h += vnoise(vec2(x * 4.0 + layerSeed, layerSeed)) * scale * 0.25;
  return baseY + h;
}
float wireframe(vec2 p) {
  vec2 g = abs(fract(p) - 0.5);
  float edge = 0.5 - min(g.x, g.y);
  return smoothstep(0.42, 0.49, edge);
}
void main() {
  vec2 uv = (gl_FragCoord.xy * 2.0 - iResolution.xy) / iResolution.y;
  float t = iTime * (0.4 + uIntensity * 0.4);
  // Tap-to-rise: the tap impulse lifts the sun toward its full-charge
  // height (the remaining headroom scales the lift, so a tap near full
  // charge nudges rather than teleports), then the host's decay eases
  // it back to the charge-determined position.
  float rise = clamp(uKwhScale + uTapRise * (1.0 - uKwhScale), 0.0, 1.0);
  float sunHeight = -0.10 + rise * 0.42;
  float sunR = 0.16 + rise * 0.20;
  vec3 zenith = vec3(0.30, 0.08, 0.50);
  vec3 mid    = vec3(0.78, 0.20, 0.55);
  vec3 horiz  = vec3(1.00, 0.32, 0.45);
  vec3 col = mix(horiz, mid, smoothstep(-0.05, 0.4, uv.y));
  col = mix(col, zenith, smoothstep(0.3, 0.95, uv.y));
  if (uv.y > 0.22) {
    vec2 sc = floor(uv * 95.0);
    float s = hash21(sc);
    if (s > 0.989) {
      float twinkle = 0.5 + 0.5 * sin(t * 3.0 + s * 100.0);
      col += vec3(1.0, 0.95, 1.0) * (s - 0.989) * 80.0 * twinkle;
    }
  }
  vec2 sunC = vec2(0.0, sunHeight);
  vec2 sunVec = (uv - sunC) / sunR;
  float sunD = length(sunVec);
  if (sunD < 1.5) {
    float glow = smoothstep(1.5, 0.95, sunD) * 0.45;
    col += vec3(1.0, 0.45, 0.30) * glow;
  }
  if (sunD < 1.0) {
    vec3 sunCol = mix(vec3(1.0, 0.88, 0.30), vec3(1.0, 0.45, 0.22), smoothstep(0.0, 1.0, sunD));
    float yLocal = sunVec.y;
    if (yLocal < 0.0) {
      float stripeY = abs(yLocal) * 14.0;
      float stripe = step(0.50, fract(stripeY));
      float stripeFade = smoothstep(-0.95, -0.05, yLocal);
      float carve = mix(1.0, stripe, stripeFade);
      sunCol *= carve;
    }
    col = mix(col, sunCol, smoothstep(1.0, 0.85, sunD));
  }
  float farM = mountainSilhouette(uv.x, 17.0, 0.18, -0.04);
  if (uv.y < farM && uv.y > -0.14) {
    vec3 farMcol = mix(vec3(0.42, 0.12, 0.48), horiz, 0.25);
    float slope = farM - mountainSilhouette(uv.x - 0.015, 17.0, 0.18, -0.04);
    farMcol *= 0.85 + slope * 6.0;
    col = mix(col, farMcol, 0.75);
  }
  float midM = mountainSilhouette(uv.x, 53.0, 0.22, -0.10);
  if (uv.y < midM && uv.y > -0.18) {
    vec3 midMcol = vec3(0.22, 0.06, 0.32);
    float slope = midM - mountainSilhouette(uv.x - 0.015, 53.0, 0.22, -0.10);
    midMcol *= 0.85 + slope * 7.0;
    col = mix(col, midMcol, 0.92);
  }
  float fgHorizon = -0.14;
  if (uv.y < fgHorizon) {
    float horizonOffset = fgHorizon - uv.y;
    float worldZ = 1.0 / max(horizonOffset, 0.001);
    float worldX = uv.x * worldZ;
    vec2 worldP = vec2(worldX, worldZ + t * 4.0);
    float h = vnoise(worldP * 0.3) * 0.6;
    float projY = -horizonOffset + h * 0.018 / worldZ;
    bool above = uv.y > projY;
    if (!above) {
      float wire = wireframe(worldP * 0.55);
      float fog = exp(-worldZ * 0.05);
      vec3 wireCol = vec3(1.0, 0.20, 0.95);
      vec3 baseCol = vec3(0.10, 0.04, 0.20);
      vec3 fgCol = mix(baseCol, wireCol, wire);
      fgCol = mix(horiz * 0.4, fgCol, fog);
      col = fgCol;
    }
  }
  col += col * col * 0.15;
  col *= uIntensity * 0.5 + 0.6;
  fragColor = vec4(col, 1.0);
}`;
